import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Text from '@/components/Text';
import { ChannelGroupResource } from '@/types/resources';

const OtherChannels = styled.button`
  display: flex;
  align-items: center;
  height: 23px;
  padding: 0 8px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 50px;
  display: inline-flex;
  margin-top: 8px;
`;

const Triangle = styled.div`
  width: 24px;
  height: 12px;
  position: absolute;
  overflow: hidden;
  top: -12px;
  left: 4px;
  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 14px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 8px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

interface BoxProps {
  $left: number;
  $top: number;
}

const Box = styled.div<BoxProps>`
  position: fixed;
  padding: 8px 12px;
  z-index: 51;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  top: ${({ $top }) => $top + 34}px;
  left: ${({ $left }) => $left + 66}px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const TooltipHolder = styled.span`
  display: inline-block;
`;

const Channel = styled(Text)`
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const modalRoot = document.getElementById('modals-root');

interface Props {
  channels: ChannelGroupResource['channels'];
}

const ChannelsTooltip: FC<Props> = ({ channels }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const ref = useRef(document.createElement('div'));

  const buttonRef = useRef<HTMLButtonElement>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const domRect = buttonRef.current?.getBoundingClientRect();

  useEffect(() => {
    const callback = () => setShowTooltip(false);
    window.addEventListener('scroll', callback, true);
    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  return (
    <>
      <TooltipHolder>
        <>
          <OtherChannels
            onMouseEnter={(e) => {
              e.stopPropagation();
              setShowTooltip(true);
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              setShowTooltip(false);
            }}
            ref={buttonRef}
          >
            <Text $type="smallHeadline">+{channels.length} others</Text>
          </OtherChannels>
          {createPortal(
            <>
              {domRect && showTooltip && (
                <Box
                  ref={componentRef}
                  $left={domRect.x - domRect.width}
                  $top={domRect.y + window.scrollY}
                >
                  <Triangle />
                  {channels.map((channel) => (
                    <Channel key={channel.id} $type="extraSmall">
                      {channel.name}
                    </Channel>
                  ))}
                </Box>
              )}
            </>,
            ref.current,
          )}
        </>
      </TooltipHolder>
    </>
  );
};

export default ChannelsTooltip;
